import { useCallback, useContext, useState } from 'react'

import { faCheckCircle, faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, AccordionDetails, AccordionSummary, Box, TextField, Theme, Typography } from '@material-ui/core'
import { makeStyles, styled } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'

import DatePicker from 'components/MaterialDatePicker'
import PhoneForm, { useMinimalErrorValues } from 'components/PhoneForm'
import { COUNTRY_CODES } from 'components/PhoneForm/country-codes.ts'
import Interests from 'components/ShareInterest/Interests.tsx'
import { selectUser } from 'store/user'

import ShareInterestContext from './ShareInterestProvider/Context'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: '1px solid #E0E0E0',
  },
  status: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gridGap: theme.spacing(1),
  },
  formBox: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
    flexDirection: 'column',
  },
  fieldBox: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  datePicker: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
}))

const AccordionSummaryNoRotate = styled(AccordionSummary)(() => ({
  '& .MuiAccordionSummary-expandIcon.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
}))

export const YourInfo = () => {
  const classes = useStyles()

  const user = useSelector(selectUser)
  const { errors, requiredFieldsIncomplete, studentInterestInfo, updateStudentInterestInfo, firstTime } =
    useContext(ShareInterestContext)

  const [expanded, setExpanded] = useState(requiredFieldsIncomplete || firstTime)

  const under13Error = errors?.dateOfBirth?.hasError && errors?.dateOfBirth?.reason === 'under-13'

  const [validation, setValidation] = useState({})
  const updateValidation = useCallback(
    (changes) =>
      setValidation({
        ...validation,
        ...changes,
      }),
    [validation],
  )

  const getErrorValue = useMinimalErrorValues(validation)

  return (
    <Box mb={1}>
      <Accordion
        elevation={0}
        className={classes.root}
        expanded={expanded}
      >
        <AccordionSummaryNoRotate
          onClick={() => setExpanded(!expanded)}
          expandIcon={
            <Typography
              variant="button"
              color="primary"
            >
              {expanded ? 'Collapse' : 'Expand'}
            </Typography>
          }
        >
          <Box>
            <Typography variant="subtitle2">Your Info</Typography>
            {requiredFieldsIncomplete ? (
              <Box
                className={classes.status}
                color="primary.main"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <Typography
                  variant="body2"
                  color="primary"
                >
                  Complete your info to share
                </Typography>
              </Box>
            ) : (
              <Box
                className={classes.status}
                color="success.dark"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <Typography variant="body2">All done and ready to share</Typography>
              </Box>
            )}
          </Box>
        </AccordionSummaryNoRotate>
        <AccordionDetails>
          <Box className={classes.formBox}>
            <Box className={classes.fieldBox}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                required
                value={studentInterestInfo?.firstName}
                error={errors?.firstName}
                onChange={(e) => updateStudentInterestInfo('firstName', e.target.value)}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                value={studentInterestInfo?.lastName}
                error={errors?.lastName}
                onChange={(e) => updateStudentInterestInfo('lastName', e.target.value)}
              />
            </Box>
            <Box className={classes.fieldBox}>
              <TextField
                label="Personal Email"
                variant="outlined"
                fullWidth
                required
                value={studentInterestInfo?.email}
                error={errors?.email}
                onChange={(e) => updateStudentInterestInfo('email', e.target.value)}
              />
              <div className={classes.datePicker}>
                <DatePicker
                  date={formatDOBForUI(studentInterestInfo?.dateOfBirth)}
                  pickerIdSuffix="dateOfBirth"
                  required
                  error={errors?.dateOfBirth?.hasError}
                  onChange={(_, value) => {
                    updateStudentInterestInfo('dateOfBirth', formatDOBForApi(value))
                  }}
                  label="Date of Birth"
                  id="studentDateOfBirth"
                  format="MM/dd/yyyy"
                  invalidDateMessage="Please enter a valid date"
                />
                {under13Error && (
                  <Typography
                    variant="caption"
                    color="error"
                  >
                    You must be at least 13 to share your info.
                  </Typography>
                )}
              </div>
            </Box>
            <PhoneForm
              label="Mobile Phone"
              phone={parsePhone(studentInterestInfo?.phoneNumber)}
              onChange={(phone: { CountryCode: number; Number: string }) => {
                if (phone.Number === '') {
                  updateStudentInterestInfo('phoneNumber', '')
                } else {
                  const phoneNumber = `+${phone.CountryCode}${phone.Number}`
                  updateStudentInterestInfo('phoneNumber', phoneNumber)
                }
              }}
              validation={validation}
              updateValidation={updateValidation}
              showTypeField={false}
              getErrorValue={getErrorValue}
              requiresPhoneFields={false}
            />
            <Interests />
            {user?.Profile.GraduationYear && (
              <Typography variant="body2">
                {user.HighSchool?.SchoolName ? user.HighSchool?.SchoolName : ''} Class of {user?.Profile.GraduationYear}
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default YourInfo

const formatDOBForApi = (date?: string | null) => {
  if (!date) return ''
  const [month, day, year] = date.split('/')
  return `${year}-${month}-${day}`
}

const formatDOBForUI = (date: string | undefined) => {
  if (!date) return ''
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

function parsePhone(numStr?: string) {
  if (!numStr) {
    return {}
  }

  for (let len = 4; len >= 1; len--) {
    // Skipping the first character, which is always a '+'
    const code = numStr.slice(1, len)
    if (COUNTRY_CODES.includes(code)) {
      return {
        CountryCode: Number(code),
        Number: numStr.slice(len),
      }
    }
  }

  return {}
}
